<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import YardAssetPresenterV2 from '@satellite/components/elements/presenters/YardAssetPresenterV2.vue';

import CheckSpotAssetSection from '@/components/CheckSpotAssetSection.vue';
import CheckSpotCarrierVisit from '@/components/CheckSpotCarrierVisit.vue';

import type { YardAssetVisitDetails } from '@satellite/composables/yardAssetVisitPresenterShared';
import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenter';
import type { OccupiedView } from '@/composables/useCheckSpotPanel';

const props = defineProps<{
  occupiedView: OccupiedView;
  hookedAsset: YardAssetDetails | null;
  unhookedAsset: YardAssetDetails | null;
  assetVisit: YardAssetVisitDetails | null;
  currentAsset?: { id: string } | null;
  showCurrentAsset?: boolean;
}>();

const { t } = useI18n();
const occupiedTab = ref<'truck' | 'asset'>('truck');

watch(
  () => [props.hookedAsset?.id, props.unhookedAsset?.id],
  () => {
    occupiedTab.value = 'truck';
  }
);
</script>

<template>
  <div class="d-flex flex-column gap-4">
    <template v-if="occupiedView === 'tabs'">
      <v-tabs v-model="occupiedTab" grow color="primary" density="comfortable">
        <v-tab value="truck" class="typography-button-lg" data-testid="check-spot-panel-tab-truck">
          {{ t('checkSpotPanel.tabs.truck') }}
        </v-tab>
        <v-tab value="asset" class="typography-button-lg" data-testid="check-spot-panel-tab-asset">
          {{ t('checkSpotPanel.tabs.asset') }}
        </v-tab>
      </v-tabs>
      <v-tabs-window v-model="occupiedTab">
        <v-tabs-window-item value="truck">
          <div class="d-flex flex-column gap-4">
            <check-spot-carrier-visit :asset-visit="assetVisit" />
            <check-spot-asset-section
              v-if="hookedAsset"
              :title="t('checkSpotPanel.sections.hookedAsset')"
              :asset="hookedAsset"
              testid="check-spot-panel-hooked-asset" />
          </div>
        </v-tabs-window-item>
        <v-tabs-window-item value="asset">
          <check-spot-asset-section
            v-if="unhookedAsset"
            :title="t('checkSpotPanel.sections.unhookedAsset')"
            :asset="unhookedAsset"
            testid="check-spot-panel-unhooked-asset" />
        </v-tabs-window-item>
      </v-tabs-window>
    </template>

    <template v-else-if="occupiedView === 'carrierVisitHooked'">
      <check-spot-carrier-visit :asset-visit="assetVisit" />
      <check-spot-asset-section
        v-if="hookedAsset"
        :title="t('checkSpotPanel.sections.hookedAsset')"
        :asset="hookedAsset"
        testid="check-spot-panel-hooked-asset" />
    </template>

    <check-spot-carrier-visit
      v-else-if="occupiedView === 'carrierVisit'"
      :asset-visit="assetVisit" />

    <check-spot-asset-section
      v-else-if="occupiedView === 'unhooked' && unhookedAsset"
      :title="t('checkSpotPanel.sections.unhookedAsset')"
      :asset="unhookedAsset"
      testid="check-spot-panel-unhooked-asset" />

    <template v-else>
      <span class="text-overline text-high-emphasis">
        {{ t('checkSpotPanel.sections.occupiedBy') }}
      </span>
      <check-spot-carrier-visit v-if="assetVisit" :asset-visit="assetVisit" />
      <v-card
        v-if="showCurrentAsset && currentAsset"
        variant="flat"
        border
        data-testid="check-spot-panel-occupied-asset">
        <v-card-text class="d-flex flex-column gap-1">
          <span class="typography-body-sm-bold">{{ t('checkSpotPanel.fields.currentAsset') }}</span>
          <yard-asset-presenter-v2
            :asset-id="currentAsset.id"
            :display-fields="['code', 'typeChip']"
            :asset-type-chip-props="{ class: 'px-2', density: 'compact' }"
            headless>
            <template #asset-code="{ assetCode }">
              <span class="typography-body-md">{{ assetCode }}</span>
            </template>
            <template #asset-type-chip="{ assetTranslatedType }">
              <span class="text-uppercase typography-chip-sm">{{ assetTranslatedType }}</span>
            </template>
          </yard-asset-presenter-v2>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>
